import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/admin/api";
import { useAuth } from "@/admin/AuthContext";
import { Eyebrow } from "@/components/comiq/Eyebrow";
import { COLORS } from "@/comiq/tokens";
import { toast } from "sonner";
import { Plus, Lock, FileEdit, Layers3, GitBranch, ShieldCheck, FileSpreadsheet, Eye, Trash2 } from "lucide-react";
import { ImportInstrumentModal } from "./ImportInstrumentModal";
import { DeleteDialog } from "./DeleteDialog";

const VER_STYLE = {
  draft: { bg: "#FEF3C7", fg: "#B45309", label: "Draf" },
  published: { bg: "#DCFCE7", fg: "#15803D", label: "Terbit (terkunci)" },
  retired: { bg: "#F1EDE4", fg: "#6A6256", label: "Pensiun" },
};

export default function Instruments() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const isRw = user?.role !== "analyst";
  const [studies, setStudies] = useState([]);
  const [studyId, setStudyId] = useState("");
  const [instruments, setInstruments] = useState([]);
  const [creating, setCreating] = useState(false);
  const [title, setTitle] = useState("");
  const [importing, setImporting] = useState(false);
  const [deleting, setDeleting] = useState(null);
  const canDelete = user?.role === "platform_admin";

  useEffect(() => {
    api.get("/admin/studies").then((r) => {
      setStudies(r.data);
      if (r.data[0]) setStudyId(r.data[0].study_id);
    }).catch(() => {});
  }, []);

  const load = (sid) => {
    if (!sid) return;
    api.get(`/admin/instruments?study_id=${sid}`).then((r) => setInstruments(r.data)).catch(() => {});
  };
  useEffect(() => load(studyId), [studyId]);

  const createInstrument = async () => {
    if (!title.trim()) return;
    try {
      const r = await api.post("/admin/instruments", { study_id: studyId, title: title.trim() });
      toast.success("Instrumen dibuat sebagai draft — lanjutkan di editor");
      setTitle(""); setCreating(false);
      navigate(`/admin/instruments/${r.data.versions[0].version_id}`);
    } catch (e) { toast.error(e.response?.data?.detail || "Gagal membuat"); }
  };

  const publish = async (versionId) => {
    try { await api.post(`/admin/versions/${versionId}/publish`); toast.success("Versi diterbitkan & dikunci"); load(studyId); }
    catch (e) { toast.error(e.response?.data?.detail || "Gagal menerbitkan"); }
  };
  const newVersion = async (versionId) => {
    try { const r = await api.post(`/admin/versions/${versionId}/new-version`); toast.success(`Draf v${r.data.version_no} dibuat`); load(studyId); navigate(`/admin/instruments/${r.data.version_id}`); }
    catch (e) { toast.error(e.response?.data?.detail || "Gagal membuat versi baru"); }
  };

  const inputCls = "rounded-comiq-sm border bg-white px-3 py-2 text-sm outline-none";

  return (
    <div className="p-6">
      <Eyebrow>Manajemen Instrumen</Eyebrow>
      <h1 className="font-serif-editorial mt-1 text-3xl" style={{ color: COLORS.ink }}>Instrumen &amp; versi</h1>
      <p className="mt-1.5 text-sm" style={{ color: COLORS.textSoft }}>Buat/edit draf, terbitkan &amp; kunci versi (checksum), lalu buat versi berikutnya. Versi terbit tidak dapat diubah — data historis aman.</p>

      <div className="mt-4 flex flex-wrap items-center gap-3">
        <select value={studyId} onChange={(e) => setStudyId(e.target.value)} className={inputCls} style={{ borderColor: COLORS.line, color: COLORS.ink }} data-testid="study-select">
          {studies.map((s) => <option key={s.study_id} value={s.study_id}>{s.title}</option>)}
        </select>
        {isRw && !creating && <button onClick={() => setCreating(true)} className="btn-comiq btn-accent" data-testid="new-instrument-btn"><Plus size={15} /> Instrumen baru</button>}
        {isRw && !creating && <button onClick={() => setImporting(true)} className="btn-comiq btn-line" data-testid="import-xlsx-btn"><FileSpreadsheet size={15} /> Import dari XLSX</button>}
        {isRw && creating && (
          <div className="flex items-center gap-2">
            <input autoFocus value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Judul instrumen" className={inputCls} style={{ borderColor: COLORS.line, color: COLORS.ink }} data-testid="instrument-title-input" />
            <button onClick={createInstrument} className="btn-comiq btn-accent" data-testid="save-instrument-btn">Simpan</button>
            <button onClick={() => { setCreating(false); setTitle(""); }} className="btn-comiq btn-line">Batal</button>
          </div>
        )}
      </div>

      <div className="mt-6 space-y-4">
        {instruments.length === 0 && <div className="rounded-comiq border p-8 text-center text-sm" style={{ borderColor: COLORS.line, color: COLORS.textSoft }}>Belum ada instrumen untuk studi ini.</div>}
        {instruments.map((inst) => (
          <div key={inst.instrument_id} className="rounded-comiq border bg-comiq-surface-alt p-5" style={{ borderColor: COLORS.line }} data-testid={`instrument-${inst.instrument_id}`}>
            <div className="flex items-center gap-2">
              <Layers3 size={18} style={{ color: COLORS.orangeInk }} />
              <h3 className="text-lg font-semibold" style={{ color: COLORS.ink }}>{inst.title}</h3>
              {canDelete && inst.versions.every((v) => v.status === "draft") && (
                <button onClick={() => setDeleting(inst.instrument_id)} className="ml-auto btn-comiq btn-line" style={{ color: "#B91C1C", borderColor: "#F3C7C0" }} data-testid={`delete-instrument-${inst.instrument_id}`}>
                  <Trash2 size={14} /> Hapus
                </button>
              )}
            </div>
            <div className="mt-3 space-y-2">
              {inst.versions.map((v) => {
                const st = VER_STYLE[v.status];
                return (
                  <div key={v.version_id} className="flex flex-wrap items-center gap-3 rounded-comiq-sm border px-3 py-2.5" style={{ borderColor: COLORS.line }} data-testid={`version-row-${v.version_no}`}>
                    <span className="font-mono text-sm font-bold" style={{ color: COLORS.ink }}>v{v.version_no}</span>
                    <span className="inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-semibold" style={{ background: st.bg, color: st.fg }} data-testid={`version-status-${v.version_no}`}>
                      {v.status === "published" && <Lock size={11} />}{st.label}
                    </span>
                    {v.checksum && <span className="inline-flex items-center gap-1 font-mono text-xs" style={{ color: COLORS.textSoft }} title={v.checksum}><ShieldCheck size={12} /> {v.checksum.slice(0, 12)}…</span>}
                    <div className="ml-auto flex gap-2">
                      <button onClick={() => navigate(`/admin/instruments/${v.version_id}/preview`)} className="btn-comiq btn-line" data-testid={`preview-version-${v.version_no}`}>
                        <Eye size={14} /> Pratinjau responden
                      </button>
                      <button onClick={() => navigate(`/admin/instruments/${v.version_id}`)} className="btn-comiq btn-line" data-testid={`open-version-${v.version_no}`}>
                        <FileEdit size={14} /> {v.status === "draft" ? "Edit" : "Lihat"}
                      </button>
                      {isRw && v.status === "draft" && <button onClick={() => publish(v.version_id)} className="btn-comiq btn-accent" data-testid={`publish-${v.version_no}`}>Terbitkan</button>}
                      {isRw && v.status === "published" && <button onClick={() => newVersion(v.version_id)} className="btn-comiq btn-line" data-testid={`new-version-${v.version_no}`}><GitBranch size={14} /> Versi berikutnya</button>}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        ))}
      </div>

      {deleting && <DeleteDialog kind="instrument" id={deleting} onClose={() => setDeleting(null)} onDeleted={() => load(studyId)} />}

      {importing && (
        <ImportInstrumentModal
          studies={studies}
          studyId={studyId}
          instruments={instruments}
          onClose={() => setImporting(false)}
          onCreated={(versionId) => { setImporting(false); load(studyId); navigate(`/admin/instruments/${versionId}`); }}
        />
      )}
    </div>
  );
}
